// DwsMetaTagEntry: http-equiv="refresh" on statically rendered pages.
// A browser keeps a refresh it has seen scheduled even after the <meta> is removed, and enhanced navigation swaps
// pages without a new document - so a live refresh element followed the user to other pages. The entry renders a
// data-dws-refresh-* marker instead, and this runs it while the marker is in the head and cancels it once it is gone.
//
// Statically rendered pages have no interactive render that could load a module, so this cannot be lazy: the core
// imports installMetaRefresh() and calls it once at startup (it was DWSBlazorBase.js's window.dwsMetaRefresh).

interface MetaRefreshState {
    timer: ReturnType<typeof setTimeout> | null;
    key: string | null;
    observer: MutationObserver | null;
}

const state: MetaRefreshState = { timer: null, key: null, observer: null };

function syncMetaRefresh(): void {
    const meta = document.head.querySelector("meta[data-dws-refresh-seconds]");
    const key = meta
        ? meta.getAttribute("data-dws-refresh-seconds") + "|" + (meta.getAttribute("data-dws-refresh-url") || "") + "|" + location.href
        : null;

    if (key === state.key) {
        return;
    }

    clearTimeout(state.timer);
    state.timer = null;
    state.key = key;

    if (!meta) {
        return;
    }

    const seconds = parseFloat(meta.getAttribute("data-dws-refresh-seconds"));

    if (!isFinite(seconds) || seconds < 0) {
        return;
    }

    const url = meta.getAttribute("data-dws-refresh-url");
    const target = url ? new URL(url, location.href).href : location.href;

    state.timer = setTimeout(function () {
        location.replace(target);
    }, seconds * 1000);
}

function initMetaRefresh(): void {
    if (state.observer || !document.head) {
        return;
    }

    state.observer = new MutationObserver(syncMetaRefresh);
    state.observer.observe(document.head, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["data-dws-refresh-seconds", "data-dws-refresh-url"]
    });

    // Enhanced navigation can keep an identical marker in place while the URL changes.
    const blazor = window.Blazor as { addEventListener?: (name: string, callback: () => void) => void } | undefined;
    if (blazor && typeof blazor.addEventListener === "function") {
        blazor.addEventListener("enhancedload", syncMetaRefresh);
    }

    syncMetaRefresh();
}

/** Starts watching the head for the refresh marker (now, or once the document has been parsed). Idempotent. */
export function installMetaRefresh(): void {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initMetaRefresh);
    } else {
        initMetaRefresh();
    }
}
